import { Form, Col, Row, Tab, TabPane, Tabs, FormLabel, FormControl, FormGroup, FormSelect } from "react-bootstrap";
import { FormControlElement, IHeader, ILanguage, ISiteMenuTree } from "../../../models/Interfaces";
import AppModal from "../../components/common/AppModal";
import { useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import SiteMediaPicker from "../../components/common/SiteMediaPicker";
import HTMLEditor from "../../components/common/HTMLEditor";
import { buildHeaderEditorFrame } from "../../components/common/editorSiteFrame";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { SiteMenuService } from "../../../services/SiteMenuService";
import { MenuType } from "../menus/Menus";
import { Core, formValidation, notEmpty } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { Excluded } from '@form-validation/plugin-excluded';
import { faSave } from "@fortawesome/free-solid-svg-icons";

interface IAddUpdateHeaderModal {
  header: IHeader;
  onClose: () => void;
  onSave: (header: IHeader) => void;
  languages: Array<ILanguage>;
};

const HeaderTabs = {
  General: "general",
  Properties: "properties"
};

const AddUpdateHeaderModal = (props: IAddUpdateHeaderModal) => {
  const [activeTab, setActiveTab] = useState<string>(HeaderTabs.General);
  const [selectedHeader, setSelectedHeader] = useState<IHeader>();
  const [languages] = useState<Array<ILanguage>>(props.languages ?? []);
  const [menus, setMenus] = useState<Array<ISiteMenuTree>>([]);

  const menuSvcRef = useRef<SiteMenuService>();
  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const saveHeader = async () => {
    if (selectedHeader && fvRef.current) {
      const validres = await fvRef.current.validate();

      if (validres === "Valid") {
        props.onSave(selectedHeader);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<FormControlElement>) : void => {
    const { value, name } = event.target;

    if (name) {
      setSelectedHeader(prev => ({
        ...prev as IHeader,
        [name]: value
      }));
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) : void => {
    const { name, checked } = event.target;

    if (name) {
      setSelectedHeader(prev => ({
        ...prev as IHeader,
        [name]: checked
      }));
    }
  };

  const updateProperty = (name: string, value: string): void => {
    if (name) {
      setSelectedHeader(prev => ({
        ...prev as IHeader,
        [name]: value
      }));
    }
  };

  const getMenus = async () : Promise<void> => {
    if (menuSvcRef.current && selectedHeader?.Language) {
      const menus = await menuSvcRef.current.getSiteMenusForLanguage(selectedHeader.Language);
      if (menus) {
        const grps = menus.filter(m => m.type === MenuType.MenuGroup);
        setMenus(grps);
      }
    }
  };

  const createValidation = () : void => {
    if (formRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Name: {
            validators: {
              notEmpty: { message: "Header name is required" }
            }
          },
          Language: {
            validators: {
              notEmpty: { message: "Please select a language for the header"}
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          excluded: new Excluded(),
        }
      });
    }
  };

  useEffect(() => {
    if (selectedHeader?.Language) {
      getMenus();
    }
  }, [selectedHeader?.Language]);

  useEffect(() => {
    setSelectedHeader(props.header);
  }, [props.header]);

  useEffect(() => {
    if (formRef.current && !fvRef.current) {
      createValidation();
    }
  }, [formRef.current]);

  useEffect(() => {

    if (!menuSvcRef.current) {
      menuSvcRef.current = new SiteMenuService();
    }

    return(() => {
      fvRef.current = null;
    });
  }, []);

  return (
    <AppModal
      onClose={props.onClose}
      onDataAction={saveHeader}
      size="xl"
      showButtons={true}
      dataActionButtonText="Save Header"
      title="Header Details"
      actionButtonIcon={faSave}
      actionButtonVariant="success"
      cancelButtonVariant="primary">

      <Form id="HeaderDetailsForm" ref={formRef}>
        <Tabs activeKey={activeTab}
          onSelect={(tk) => setActiveTab(tk ?? HeaderTabs.General)}>

          <Tab eventKey={HeaderTabs.General} title="General">
            <TabPane active={activeTab === HeaderTabs.General}>
              <Row>
                <Col md={12} xl={6} className="mt-3">
                  <FormLabel>Name</FormLabel>
                  <FormControl
                    type="text"
                    name="Name"
                    id="Name"
                    value={selectedHeader?.Name ?? ""}
                    onChange={handleInputChange}
                  />
                </Col>
                <Col md={12} xl={6} className="mt-3">
                  <FormLabel htmlFor="Language">Language</FormLabel>
                  <FormSelect
                    value={selectedHeader?.Language ?? ""}
                    onChange={handleInputChange}
                    title="select a language for the header"
                    name="Language"
                    id="Language">
                    <option value="">select a language...</option>
                    {
                      languages.map(lng => (
                        <option key={`LANG-${lng.Id}`} value={lng.ShortCode}>{lng.Language}</option>
                      ))
                    }
                  </FormSelect>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mt-3">
                  <SiteMediaPicker
                    onMediaClear={() => updateProperty("HeaderImageUrl", "")}
                    onMediaSelect={(selected) => updateProperty("HeaderImageUrl", selected)}
                    existingUrl={selectedHeader?.HeaderImageUrl ?? ""}
                    key={"HEADERIMAGE"} />
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mt-3">
                  <HTMLEditor documentKey={selectedHeader?.Id}
                    value={selectedHeader?.HeaderText ?? ""}
                    name="HeaderText"
                    id="HeaderText"
                    onInputChange={handleInputChange}
                    siteFrame={buildHeaderEditorFrame(selectedHeader ?? props.header)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12} lg={2} className="mt-3">
                  <FormLabel htmlFor="ShowMenu">Show Menu</FormLabel>
                  <FormCheckInput
                    className="form-check"
                    checked={selectedHeader?.ShowMenu ?? false}
                    onChange={handleCheckChange}
                    id="ShowMenu"
                    name="ShowMenu">
                  </FormCheckInput>
                </Col>
                <Col md={12} lg={5} className="mt-3">
                  <FormLabel htmlFor="MenuGroupId">Menu (optional)</FormLabel>
                  <FormSelect
                    disabled={menus?.length <= 0}
                    title="select optional menu"
                    name="MenuGroupId"
                    id="MenuGroupId"
                    value={selectedHeader?.MenuGroupId ?? ""}
                    onChange={handleInputChange}>
                    <option value="">Select menu for header...</option>
                    {
                      menus.map(m => (
                        <option key={`MENU-${m.id}`} value={m.id}>{m.text}</option>
                      ))
                    }
                  </FormSelect>
                </Col>
                <Col md={12} lg={5} className="mt-3">
                  <FormLabel htmlFor="MenuAlign">Menu Position</FormLabel>
                  <FormSelect
                    disabled={!(selectedHeader?.MenuGroupId)}
                    title="Menu position for header"
                    name="MenuAlign"
                    id="MenuAlign"
                    value={selectedHeader?.MenuAlign ?? ""}
                    onChange={handleInputChange}>
                    <option value="">Select menu position...</option>
                    <option value="TOP">Above Header</option>
                    <option value="BOTTOM">Below Header</option>
                    <option value="LEFT">After Header</option>
                    <option value="RIGHT">Before Header</option>
                  </FormSelect>
                </Col>
              </Row>
            </TabPane>
          </Tab>

          <Tab eventKey={HeaderTabs.Properties} title="Class Properties">
            <TabPane active={activeTab === HeaderTabs.Properties}>
              <Row>
                <Col md={12} xl={6} className="mt-3">
                  <FormLabel>Header Container Class (bootstrap or custom css)</FormLabel>
                  <FormControl
                    type="text"
                    name="HeaderContainerClass"
                    id="HeaderContainerClass"
                    value={selectedHeader?.HeaderContainerClass ?? ""}
                    onChange={handleInputChange}
                    placeholder="header contaienr class...">
                  </FormControl>
                </Col>
                <Col md={12} xl={6} className="mt-3">
                  <FormLabel>Menu Container Class (bootstrap or custom css)</FormLabel>
                  <FormControl
                    type="text"
                    name="MenuContainerClass"
                    id="MenuContainerClass"
                    value={selectedHeader?.MenuContainerClass ?? ""}
                    onChange={handleInputChange}
                    placeholder="menu container class...">
                  </FormControl>
                </Col>
              </Row>
              <Row>
                <Col md={12} xl={6} className="mt-3">
                  <FormLabel>Image Container class (bootstrap or custom css)</FormLabel>
                  <FormControl
                    type="text"
                    name="HeaderImageContainerClass"
                    id="HeaderImageContainerClass"
                    value={selectedHeader?.HeaderImageContainerClass ?? ""}
                    onChange={handleInputChange}
                    placeholder="container class for header image...">
                  </FormControl>
                </Col>
                <Col md={12} xl={6} className="mt-3">
                  <FormLabel>Text Container class (bootstrap or custom css)</FormLabel>
                  <FormControl
                    type="text"
                    name="HeaderTextContainerClass"
                    id="HeaderTextContainerClass"
                    value={selectedHeader?.HeaderTextContainerClass ?? ""}
                    onChange={handleInputChange}
                    placeholder="container class for header text/html...">
                  </FormControl>
                </Col>
              </Row>
            </TabPane>
          </Tab>
        </Tabs>
      </Form>
    </AppModal>
  );
}

export default AddUpdateHeaderModal;
